/**
 * AuthGuard — layout route component for protected sections.
 *
 * Category: layout (auth guard)
 *
 * CRITICAL PATTERN: Initialize with isLoading check BEFORE isAuthenticated.
 * This prevents flashing the login page while the auth token is being
 * validated (e.g. during token refresh on page load).
 *
 * Renders:
 *   isLoading: true  → skeleton/spinner (never flash login page)
 *   isAuthenticated  → <Outlet /> (protected content)
 *   !isAuthenticated → redirect to /login
 *
 * @spec SPEC:ENGINEERING:FRONTEND:AUTH
 */
import { Outlet, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useAuth } from '@/context/AuthContext'

export function AuthGuard() {
  const { isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      void navigate({ to: '/login' })
    }
  }, [isLoading, isAuthenticated, navigate])

  // Show skeleton while auth state is resolving — never flash the login page
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center" aria-live="polite" aria-label="Loading">
        <div className="flex flex-col items-center gap-3">
          <div
            className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"
            aria-hidden="true"
          />
          <p className="text-sm text-muted-foreground">Loading…</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    // Redirect is in-flight via the useEffect above
    return null
  }

  return <Outlet />
}
